/**
 * Die Rechnung hinter der **Auswahl-Hülle** (#293), ohne DOM: welche Blöcke es gibt, welche Fläche
 * welchen Block übernimmt, und in welcher Reihenfolge dazukommende Zeilen nachziehen.
 *
 * Nachgebaut aus dem Prototyp `tools/selection-merge-prototype` (Variante X, `hullRuns` und
 * `updateHulls`), dort Szene für Szene geprüft.
 */

/** Ein Rechteck relativ zur Liste, in Pixeln. */
export interface HullRect {
  top: number
  left: number
  width: number
  height: number
}

/** Ein zusammenhängender Block markierter Zeilen. */
export interface HullRun {
  ids: readonly string[]
  rect: HullRect
  /** Steinfarben nur, wenn ALLE Zeilen ruhen — sonst färbte die erste Zeile den ganzen Block. */
  dormant: boolean
}

/** Eine Fläche, wie sie gerade dasteht oder hinfährt. */
export interface HullEntry {
  key: string
  ids: readonly string[]
  rect: HullRect
  dormant: boolean
  /** Blendet aus und wird danach entfernt — oder lebt wieder auf, wenn ihr Block zurückkommt. */
  dying: boolean
  /** Seit wann sie ausblendet (`now` beim Planen) — eben erst, darf sie noch wandern. */
  diedAt?: number
  /**
   * Wo eine neue Fläche anfängt: die alte Geometrie der Fläche, von der sie sich abteilt. Ohne
   * `from` blendet sie an ihrem Ziel ein.
   */
  from?: HullRect
}

/**
 * Mehr Abstand zwischen zwei markierten Nachbarn als das, und der Block teilt sich: Dazwischen steht
 * etwas, das nicht dazugehört (die Inline-Eingabe, eine ausfahrende Zeile). Die Listen haben 2px
 * Abstand (Aufgaben) bzw. keinen (Unteraufgaben); der Rest ist Spiel für halbe Pixel.
 */
export const JOIN_GAP_PX = 4

export interface HullRunsInput {
  /** Alle Zeilen der Liste in der Reihenfolge, in der sie dastehen. */
  rowIds: readonly string[]
  /** Was in der Hülle liegt: die markierten Zeilen ohne die aufgeklappte. */
  inIds: ReadonlySet<string>
  dormantIds?: ReadonlySet<string>
  /** Gemessene Rechtecke. Eine Zeile ohne Maß (ausgeblendet, Höhe 0) unterbricht den Block. */
  rects: ReadonlyMap<string, HullRect>
}

/**
 * @example
 * hullRuns({ rowIds: ['a', 'b', 'c'], inIds: new Set(['a', 'b']), rects })
 * // → [{ ids: ['a', 'b'], rect: <a ∪ b>, dormant: false }]
 */
export function hullRuns({ rowIds, inIds, dormantIds, rects }: HullRunsInput): HullRun[] {
  const runs: { ids: string[]; rects: HullRect[]; dormant: boolean }[] = []
  let open = false
  for (const id of rowIds) {
    const rect = rects.get(id)
    if (!inIds.has(id) || !rect) {
      open = false
      continue
    }
    const dormant = dormantIds?.has(id) ?? false
    const last = runs[runs.length - 1]
    const prev = last?.rects[last.rects.length - 1]
    if (open && prev && rect.top - (prev.top + prev.height) <= JOIN_GAP_PX) {
      last.ids.push(id)
      last.rects.push(rect)
      last.dormant = last.dormant && dormant
    } else {
      runs.push({ ids: [id], rects: [rect], dormant })
    }
    open = true
  }
  return runs.map(run => ({ ids: run.ids, rect: unionRect(run.rects), dormant: run.dormant }))
}

/**
 * Eingerückte Unteraufgaben: Die Fläche reicht von der äußersten Zeile bis zum rechten Rand
 * (Sven, 08.10.2026) — die Vereinigung aller Rechtecke.
 */
function unionRect(rects: readonly HullRect[]): HullRect {
  const top = Math.min(...rects.map(r => r.top))
  const left = Math.min(...rects.map(r => r.left))
  const bottom = Math.max(...rects.map(r => r.top + r.height))
  const right = Math.max(...rects.map(r => r.left + r.width))
  return { top, left, width: right - left, height: bottom - top }
}

function overlap(a: readonly string[], b: readonly string[]): number {
  const set = new Set(a)
  return b.reduce((n, id) => (set.has(id) ? n + 1 : n), 0)
}

/**
 * So kurz nach dem Ausblenden darf eine Fläche noch zu einem neuen Block wandern. Abwählen und
 * Anwählen sind manchmal zwei Schritte: In Wiederkehrend hat jede Gruppe ihre eigene Auswahl, ein Klick
 * in die andere wählt beim Drücken ab und beim Loslassen an.
 */
export const WANDER_GRACE_MS = 200

export interface PlanHullsOptions {
  /** Schlüssel für eine neue Fläche. */
  makeKey: () => string
  /** Jetzt, in ms (`performance.now()`). Ohne Angabe wandert nur, was noch steht. */
  now?: number
  /**
   * Nur das Layout hat sich geändert: Neue Flächen stehen sofort da, und was dabei wegfällt, ist
   * sofort weg. Was schon ausblendet, blendet weiter aus.
   */
  instant?: boolean
}

/**
 * Welche Fläche welchen Block übernimmt.
 *
 * - **Wachsen, Schrumpfen:** Die Fläche mit den meisten gemeinsamen Zeilen fährt weiter.
 * - **Wandern:** Hat ein Block keine gemeinsame Zeile mit einer Fläche, und bliebe sonst eine Fläche
 *   übrig, fährt die nächstgelegene zu ihm. Ein Klick auf eine andere Zeile zieht die Markierung
 *   dorthin, statt sie hier aus- und dort einzublenden (Sven, 09.10.2026).
 * - **Teilen:** Der zweite Teil bekommt eine neue Fläche, die an der alten Geometrie anfängt — so
 *   weichen beide Hälften auseinander, statt dass eine aus dem Nichts erscheint.
 * - **Verschmelzen:** Eine Fläche übernimmt den ganzen Block; die andere fährt mit hinein und
 *   blendet dabei aus.
 * - **Wiederbeleben:** Eine ausblendende Fläche, deren Zeilen zurückkommen, lebt wieder auf, statt
 *   dass daneben eine neue entsteht (Prototyp, Fix 4).
 *
 * Ändert sich nichts, kommt `prev` selbst zurück — dann rendert auch nichts neu.
 *
 * @example
 * const next = planHulls(entries, hullRuns(input), { makeKey: () => `hull-${n++}` })
 */
export function planHulls(
  prev: readonly HullEntry[],
  runs: readonly HullRun[],
  { makeKey, instant = false, now }: PlanHullsOptions,
): readonly HullEntry[] {
  const live = prev.filter(entry => !entry.dying)
  const dying = prev.filter(entry => entry.dying)
  // Lebende zuerst, dann ausblendende: Die werden wiederbelebt statt ersetzt.
  const pool = [...live, ...dying]
  const used = new Set<HullEntry>()
  const next: HullEntry[] = []

  const take = (entry: HullEntry, run: HullRun): HullEntry => {
    used.add(entry)
    return { key: entry.key, ids: run.ids, rect: run.rect, dormant: run.dormant, dying: false, from: entry.from }
  }

  // Erst nach gemeinsamen Zeilen …
  const matched = new Map<HullRun, HullEntry>()
  for (const run of runs) {
    let best: HullEntry | null = null
    let bestOverlap = 0
    for (const entry of pool) {
      if (used.has(entry)) continue
      const n = overlap(entry.ids, run.ids)
      if (n > bestOverlap) {
        best = entry
        bestOverlap = n
      }
    }
    if (best) matched.set(run, take(best, run))
  }

  // … dann wandert, was sonst ausblendete, zum nächsten Block, der sonst neu entstünde. Steht keine
  // Fläche mehr, darf eine eben erst ausblendende (`WANDER_GRACE_MS`).
  const fresh = dying.filter(entry => now !== undefined && entry.diedAt !== undefined && now - entry.diedAt <= WANDER_GRACE_MS)
  for (const run of runs) {
    if (matched.has(run) || pool.some(entry => overlap(entry.ids, run.ids) > 0)) continue
    const nearestOf = (candidates: readonly HullEntry[]) => {
      let nearest: HullEntry | null = null
      for (const entry of candidates) {
        if (used.has(entry)) continue
        if (!nearest || distance(entry.rect, run.rect) < distance(nearest.rect, run.rect)) nearest = entry
      }
      return nearest
    }
    const nearest = nearestOf(live) ?? nearestOf(fresh)
    if (nearest) matched.set(run, take(nearest, run))
  }

  for (const run of runs) {
    const entry = matched.get(run)
    if (entry) {
      next.push(entry)
      continue
    }
    const source = instant ? undefined : pool.find(e => overlap(e.ids, run.ids) > 0)
    next.push({
      key: makeKey(),
      ids: run.ids,
      rect: run.rect,
      dormant: run.dormant,
      dying: false,
      ...(source ? { from: source.rect } : {}),
    })
  }

  if (!instant) {
    for (const entry of live) {
      if (used.has(entry)) continue
      // Verschmilzt sie, fährt sie in den neuen Block hinein, statt an ihrem Platz zu verblassen.
      const into = runs.find(run => overlap(entry.ids, run.ids) > 0)
      next.push({ ...entry, rect: into ? into.rect : entry.rect, dying: true, ...(now !== undefined ? { diedAt: now } : {}) })
    }
  }
  for (const entry of dying) {
    if (!used.has(entry)) next.push(entry)
  }

  return sameHulls(prev, next) ? prev : next
}

/** Abstand der Mitten zweier Rechtecke, senkrecht. */
function distance(a: HullRect, b: HullRect): number {
  return Math.abs(a.top + a.height / 2 - (b.top + b.height / 2))
}

const EPSILON = 0.1

function sameRect(a: HullRect, b: HullRect): boolean {
  return Math.abs(a.top - b.top) < EPSILON
    && Math.abs(a.left - b.left) < EPSILON
    && Math.abs(a.width - b.width) < EPSILON
    && Math.abs(a.height - b.height) < EPSILON
}

function sameHulls(a: readonly HullEntry[], b: readonly HullEntry[]): boolean {
  if (a.length !== b.length) return false
  return a.every((entry, i) => {
    const other = b[i]
    return entry.key === other.key
      && entry.dying === other.dying
      && entry.dormant === other.dormant
      && entry.ids.length === other.ids.length
      && entry.ids.every((id, j) => id === other.ids[j])
      && sameRect(entry.rect, other.rect)
  })
}

/**
 * In welcher Reihenfolge die Schrift dazukommender Zeilen nachzieht: geordnet nach dem Abstand zur
 * nächsten Zeile, die schon dabei war; gleich weit weg heißt gleicher Rang. War noch keine dabei,
 * zählt der Abstand zur obersten neuen Zeile. Nur das Dazukommen wird gestaffelt (Prototyp, Fix 5).
 *
 * **Rang ist die Stufe, nicht der Abstand:** Eine einzelne ferne Zeile (⌘-Klick) zieht sofort nach,
 * statt 30ms je Zeile dazwischen zu warten, während ihre Fläche längst steht.
 *
 * @example
 * staggerRanks(['a', 'b', 'c', 'd'], new Set(['a']), new Set(['a', 'b', 'c', 'd']))
 * // → Map { 'b' → 0, 'c' → 1, 'd' → 2 }
 */
export function staggerRanks(
  rowIds: readonly string[],
  before: ReadonlySet<string>,
  after: ReadonlySet<string>,
): Map<string, number> {
  const ranks = new Map<string, number>()
  const indexOf = new Map(rowIds.map((id, i) => [id, i]))
  const kept = rowIds.filter(id => before.has(id) && after.has(id)).map(id => indexOf.get(id)!)
  const added = rowIds.filter(id => after.has(id) && !before.has(id))
  if (added.length === 0) return ranks
  const firstAdded = indexOf.get(added[0])!
  const distances = new Map(added.map(id => {
    const i = indexOf.get(id)!
    return [id, kept.length === 0 ? i - firstAdded : Math.min(...kept.map(k => Math.abs(k - i)))]
  }))
  const steps = [...new Set(distances.values())].sort((a, b) => a - b)
  for (const [id, d] of distances) ranks.set(id, steps.indexOf(d))
  return ranks
}
